import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  AlertComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  ContainerComponent,
  SpinnerComponent
} from '@coreui/angular-pro';
import { BootstrapService, LegalService } from 'app';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe } from 'i18n';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LegalDocument, Logger, LogoComponent, SafeHtmlPipe } from 'shared';

/** A document plus the body we fetched for it and whether the user has ticked it. */
interface GateDocument {
  document: LegalDocument;
  body: string;
  checked: boolean;
}

/**
 * Mandatory legal acknowledgement gate.
 *
 * Renders whatever documents the backend says are outstanding — Terms, Privacy and the
 * Platform Acknowledgement today, anything the registry adds tomorrow — each with its
 * own unticked checkbox, and unlocks the dashboard once every required box is ticked.
 *
 * Mounted under authGuard but OUTSIDE DefaultLayoutComponent (like profile/complete),
 * so legalAcknowledgementGuard on the layout does not bounce this page into a loop.
 * Also excluded from RouteService's navigation hijacking — see
 * legalAcknowledgePathRegex there.
 */
@Component({
  selector: 'app-legal-acknowledgement-page',
  standalone: true,
  imports: [
    CommonModule,
    AlertComponent,
    ButtonDirective,
    CardComponent,
    CardBodyComponent,
    ContainerComponent,
    SpinnerComponent,
    LogoComponent,
    SafeHtmlPipe,
    TranslatePipe
  ],
  templateUrl: './legal-acknowledgement-page.component.html',
  styleUrls: ['./legal-acknowledgement-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LegalAcknowledgementPageComponent {
  private legalService = inject(LegalService);
  private authService = inject(AuthService);
  private bootstrapService = inject(BootstrapService);
  private i18nService = inject(I18nService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('LegalAcknowledgementPageComponent');

  protected loading = signal(true);
  protected submitting = signal(false);
  /** Fetching the documents failed — distinct from failing to *record* an acceptance. */
  protected loadError = signal(false);
  /** Set when the published text changed while this page was open (HTTP 409). */
  protected versionConflict = signal(false);
  /**
   * Translation key for a failed submission, or null. Kept separate from loadError:
   * reporting "documents could not be loaded" after a rejected *save* sends whoever
   * hits it looking in entirely the wrong place.
   */
  protected submitError = signal<string | null>(null);
  protected documents = signal<GateDocument[]>([]);

  /** No pre-selected checkboxes: every required document must be actively ticked. */
  protected canSubmit = computed(() =>
    this.documents().length > 0 &&
    this.documents().every(entry => entry.checked || !entry.document.required) &&
    !this.submitting()
  );

  constructor() {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.versionConflict.set(false);

    this.legalService.getRequirements().pipe(
      switchMap(requirements => {
        if (!requirements.documents.length) {
          return of([] as GateDocument[]);
        }
        // Bodies load in parallel; the gate is all-or-nothing, so there is no value in
        // revealing it before every document can be read.
        return forkJoin(
          requirements.documents.map(document =>
            this.legalService.getDocumentBody(document).pipe(
              catchError(error => {
                this.logger.error(`Failed to load document '${document.key}'`, error);
                return of('');
              }),
              map(body => ({ document, body, checked: false }) as GateDocument)
            )
          )
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: entries => {
        // A document whose body failed to load must not be acknowledgeable — the user
        // would be confirming text they were never shown.
        if (entries.some(entry => !entry.body)) {
          this.loadError.set(true);
          this.loading.set(false);
          return;
        }

        // Nothing outstanding (e.g. accepted in another tab): don't strand the user here.
        if (!entries.length) {
          this.leaveGate();
          return;
        }

        this.documents.set(entries);
        this.loading.set(false);
      },
      error: error => {
        this.logger.error('Failed to load legal requirements', error);
        this.loadError.set(true);
        this.loading.set(false);
      }
    });
  }

  protected toggle(key: string): void {
    this.documents.update(entries =>
      entries.map(entry =>
        entry.document.key === key ? { ...entry, checked: !entry.checked } : entry
      )
    );
  }

  protected onAccept(): void {
    if (!this.canSubmit()) {
      return;
    }
    this.submitting.set(true);
    this.versionConflict.set(false);
    this.submitError.set(null);

    // Only ticked documents are submitted. Required ones are all ticked by the time
    // canSubmit() allows this, but an optional document (registry `required: false`)
    // can be left unticked — sending it anyway would record, immutably, a consent the
    // user deliberately withheld.
    const consented = this.documents().filter(entry => entry.checked).map(entry => entry.document);

    this.legalService.accept(consented).pipe(
      // Re-bootstrap so legal_acknowledgement_required is refreshed in auth state;
      // the guard reads that flag, so without this the gate would re-trigger.
      switchMap(() => this.bootstrapService.bootstrap()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => this.leaveGate(),
      error: error => {
        this.submitting.set(false);
        if (error?.status === 409) {
          // The document changed under us. Reload so the user reads and confirms the
          // text that is actually published now.
          this.logger.warn('Document version conflict; reloading', error?.error);
          this.versionConflict.set(true);
          this.load();
          return;
        }
        this.logger.error('Failed to record acknowledgement', error);
        // 403 = the session is impersonating a user; consent may only be given by the
        // account holder. legal_acknowledgement_required? already suppresses the gate
        // for impersonated sessions, so this should be unreachable from the UI — say
        // something true rather than something misleading if it ever is.
        this.submitError.set(
          error?.status === 403
            ? 'legal.acknowledge.impersonation_blocked'
            : 'legal.acknowledge.submit_error'
        );
      }
    });
  }

  /** Declining is not an error state — the user simply cannot proceed while signed in. */
  protected onDecline(): void {
    this.authService.logout().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.router.navigate(['/login']),
      error: () => this.router.navigate(['/login'])
    });
  }

  private leaveGate(): void {
    const home = localStorage.getItem('homeUrl');
    this.router.navigateByUrl(home || `/${this.i18nService.getLanguage()}/dashboard`);
  }
}
